/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-undef */

import { Auth0Client, LocalStorageCache } from '@auth0/auth0-spa-js';
import publicIp from 'public-ip';
import {
  getLocallyStoredAccessToken,
  getLocallyStoredIdToken,
} from 'utils/authUtils';
import { AUTH0_CONFIG, ERROR_MESSAGE_PARAM } from './utils/commonConstants';
import { parseJwt } from './utils/commonFunctions';

const { REACT_APP_WEBSITE_URL } = process.env;

export default class Auth {
  Auth0Client = new Auth0Client(AUTH0_CONFIG);

  LocalStorageCache = new LocalStorageCache();

  accessToken!: string | null;

  idToken!: string | null;

  expiresAt!: number;

  tokenRenewalTimeout: NodeJS.Timeout | null = null;

  /**
   * Initialize Auth variables.
   */
  constructor() {
    if (
      localStorage.getItem('access_token') &&
      localStorage.getItem('id_token') &&
      localStorage.getItem('expires_at')
    ) {
      this.accessToken = localStorage.getItem('access_token');
      this.idToken = localStorage.getItem('id_token');
      this.expiresAt = parseFloat(localStorage.getItem('expires_at') as string);
    }
    // added access token, id token, expired at in class Auth for refresh token functionality to work properly
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.renewSession = this.renewSession.bind(this);
    this.setSession = this.setSession.bind(this);
    this.clearLocalStorageValuesWithKeys =
      this.clearLocalStorageValuesWithKeys.bind(this);
  }

  isScopePresent = (scope: string, getLength = false) => {
    const accessToken = localStorage.getItem('access_token');

    if (accessToken) {
      const parse = parseJwt(accessToken);

      const scopes =
        parse['https://vointell.prodigaltech.com/claims/permissions'];
      if (getLength) {
        return scopes.length;
      }
      // const scopeItems = scopes.split(' ');
      if (scopes?.find((s: string) => s === scope) !== undefined) {
        return true;
      }
      return false;
    }
    return false;
  };

  scheduleRenewal = () => {
    if (window.location.pathname === '/callback') {
      return;
    }
    const expiresAt = parseFloat(
      (localStorage.getItem('expires_at') as string) || '0',
    );
    if (!expiresAt) {
      return;
    }

    const timeout = expiresAt - Date.now();
    // we will refresh token before 1 minute of expiry
    // this is the case when user is already logged in and using our product
    if (timeout > 60000) {
      clearTimeout(this.tokenRenewalTimeout as NodeJS.Timeout);
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewSession(null, false);
      }, timeout - 60000);
    } else {
      // this is the case when user tries to access after token is expired
      localStorage.setItem('redirect_location', window.location.pathname);
      this.renewSession(null, true);
      // passing true to reload page as token is expired
    }
  };

  /**
   * Performs the call if authenticated and reloads if requested.
   * @param {Callback} callback
   * @param {Boolean} reload
   */
  renewSession = (callback: any, reload = false) => {
    // Fetch the access token silently. This has been modified to use `getTokenSilently` of AuthClient instead of `checkSession` of WebAuth
    this.Auth0Client.getTokenSilently()
      .then((_authToken: string) => {
        this.handleAuthentication(reload);
        if (callback) {
          callback();
        }
      })
      .catch((error) => {
        console.error('Error renewing session', error);
        this.logout(`Session renewal failed! Please login again: ${error}`);
      });
  };

  async handleAuthentication(isFreshLogin = false) {
    // The code reaches here after `/callback` from Auth0
    (async () => {
      const ipv4 = await publicIp.v4();
      localStorage.setItem('ipv4', ipv4);
    })();

    // Fetch access token that is automatically set by Auth0
    // Add backward compatibility to set the access token in local storage.
    try {
      const accessToken = getLocallyStoredAccessToken();
      const expiresAtSecs = getLocallyStoredAccessToken('expiresAt');
      const idToken = getLocallyStoredIdToken();
      this.setSession(accessToken, idToken, expiresAtSecs, isFreshLogin);
    } catch (error) {
      // the code won't reach here, but being safe to add this in case the `setSession` method runs into an error .
      console.error('Error handling authentication', error);
      this.logout(`Authentication failed! Please try again: ${error}`);
    }
  }

  setSession(
    accessToken: string,
    idToken: string,
    expiresAtSecs: number,
    isFreshLogin: boolean,
  ) {
    localStorage.setItem('isLoggedIn', 'true');
    const expiresAt = expiresAtSecs * 1000;
    this.accessToken = accessToken;
    this.idToken = idToken;

    // NOTE: Should we validate the supervisorSubscriptions and non-empty agent Id for the user? Maybe we can check this in future, but for now leaving it as it was working before.

    // updating field values of access token , expires at, id token in class Auth for refresh token to work properly
    localStorage.setItem('id_token', idToken);
    localStorage.setItem('expires_at', JSON.stringify(expiresAt));
    localStorage.setItem('access_token', accessToken);

    this.scheduleRenewal();
    if (isFreshLogin) {
      const redirectLocation = localStorage.getItem('redirect_location');
      if (redirectLocation && redirectLocation !== '/callback') {
        window.location.replace(redirectLocation);
      } else {
        window.location.replace('/');
      }
    }
    localStorage.removeItem('redirect_location');
  }

  clearLocalStorageValuesWithKeys(keys: any) {
    keys.forEach((key: any) => localStorage.removeItem(key));
  }

  /**
   * deletes local storage.
   */
  clearLocalStorage() {
    const arrayOfKeysToDelete = [
      'isLoggedIn',
      'access_token',
      'id_token',
      'expires_at',
    ];

    this.clearLocalStorageValuesWithKeys(arrayOfKeysToDelete);
    this.deleteAllCookies();
  }

  /**
   * Deletes all cookies from browser safely.
   */
  deleteAllCookies() {
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

  /**
   * Logs out user on request.
   * @param {Boolean} bool : Clear Local Storage or not.
   */
  logout(errorMessage = '') {
    this.clearLocalStorage();
    let returnTo = `${encodeURI(REACT_APP_WEBSITE_URL as string)}`;
    if (errorMessage) {
      returnTo = encodeURI(
        `${REACT_APP_WEBSITE_URL as string}?${ERROR_MESSAGE_PARAM}=${encodeURI(
          errorMessage,
        )}`,
      );
    }
    this.Auth0Client.logout({
      logoutParams: {
        returnTo,
      },
    });
  }

  /**
   * returns true if session is valid and still usable else, returns false.
   * @returns {Boolean}
   */
  isAuthenticatedNoRenew() {
    return this.isAuthenticated();
  }

  /**
   * returns true if session is valid and still usable else, returns false.
   * @returns {Boolean}
   */
  isAuthenticated() {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      const expiresAt = JSON.parse(
        localStorage.getItem('expires_at') as string,
      );
      if (new Date().getTime() < expiresAt) {
        return true;
      }
    }
    return false;
  }
}
